<template lang="html">
  <div
    :class="[
      media ? null : 'news-list-item--small',
    ]"
    class="news-list-item"
  >
    <AppLink
      :is-link="!!resource.url"
      :to="resource.url"
      class="news-list-item__link-wrapper"
    >
      <Media
        v-if="resource.columns.teaser_asset"
        :path="resource.columns.teaser_asset"
        :small="resource.columns.teaser_asset_mobile"
        :class="`news-list-item__media--${resource.columns.focus}`"
        class="news-list-item__media"
      />
      <div class="news-list-item__content">
        <h3 class="news-list-item__headline">
          <u class="news-list-item__headline-underline">{{ resource.columns.title }}</u>
        </h3>
        <p class="news-list-item__paragraph">
          {{ resource.columns.teaser_text }}
        </p>
        <div class="news-list-item__focus-wrapper">
          <span
            :class="[
              'news-list-item__focus',
              `news-list-item__focus--${resource.columns.focus}`
            ]"
          >
            {{ focus }}
          </span>
          <span v-if="resource.columns.date" class="news-list-item__date">
            {{ date }}
          </span>
        </div>
      </div>
    </AppLink>
  </div>
</template>

<script>
import { upperFirst } from 'lodash'
import formatDate from 'beisheim/utils/format-date'
import Media from 'beisheim/components/elements/media'
import AppLink from 'beisheim/components/elements/app-link'
import editable from '@i22/frontend-cms-utils/mixins/editable'
import checkViewport from 'beisheim/mixins/check-viewport'

export default {
  components: {
    Media,
    AppLink
  },
  mixins: [editable, checkViewport],
  computed: {
    media () {
      return false
    },
    focus () {
      return this.resource.columns.focus && this.resource.columns.focus !== 'none' ? upperFirst(this.resource.columns.focus) : 'Allgemein'
    },
    date () {
      if (!this.resource.columns.date) { return false }
      return formatDate(this.resource.columns.date)
    }
  },
  watch: {
    elBoundingRect () {
      if (this.id === 0 || this.id === 1) { return }
      if (this.scrolledBehind) {
        this.$el.style.transform = ''
        this.$el.style.opacity = 1
      } else {
        this.$el.style.transform = 'translateY(100px)'
        this.$el.style.opacity = 0
      }
    }
  }
}
</script>

<style lang="scss">
  @import 'beisheim/scss/grid';
  @import 'beisheim/scss/scale';
  @import 'beisheim/scss/colors';
  @import 'beisheim/scss/font';
  @import 'beisheim/scss/font-sizes';
  @import 'beisheim/scss/breakpoints';

  $image-mobile-height: 100vw * 0.55;
  $component-breakpoint: 5;
  $component: '.news-list-item';

  #{$component} {
    position: relative;
    display: flex;
    align-items: center;
    transition: transform 800ms cubic-bezier(.17,.67,.29,1), opacity 400ms ease-out;

    &--small {
      @include from(4) {
        min-height: 400px;
      }
    }

    &__headline {
      @include font(2);
      line-height: 2.6rem;
      margin-top: scale(6);
      margin-bottom: scale(6);
      @include from(3) {
        @include font(1.5);
        margin-top: scale(3);
        margin-bottom: scale(3);
      }
    }

    &__headline-underline {
      text-decoration: none;
      border-bottom: 1px solid gray(1);
      transition: color 100ms ease-in-out, border-bottom-color 100ms ease-in-out;

      &:hover {
        color: gray(2);
        border-bottom-color: gray(2);
      }
    }

    &__quote {
      @include font(3);
      line-height: 3rem;
      word-wrap: break-word;
      color: gray(4);

      &--bildung {
        color: brand(3);
      }
      &--gesundheit {
        color: brand(4);
      }
      &--kultur {
       color: brand(1);
      }
      &--sport {
        color: brand(2);
      }
    }

    &__media {
      width: 100%;
      @include grid-spacing(padding-right, 2);
      overflow: hidden;

      img {
        transition: transform 400ms cubic-bezier(.24,0,.47,1);
      }

      @include from($component-breakpoint) {
        @include grid-spacing(padding-right, 1);
      }
    }

    &__focus-wrapper {
      margin-top: scale(6);
      @include from($component-breakpoint) {
        margin-top: scale(4);
      }
    }

    &__date {
      color: gray(2);
      float: right;
    }

    &__focus {
      position: relative;
      @include font(0.5);
      color: gray(2);
      padding: scale(2) scale(3);

      &::before {
        display: inline-block;
        content: '';
        position: relative;
        width: scale(3);
        height: scale(3);
        border-radius: 50%;
        background-color: gray(4);
      }

      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: gray(4);
        border-radius: scale(4);
        transition: transform 200ms ease-out, opacity 100ms ease-out;
        opacity: 0;
        transform: scaleX(1.2) scaleY(1.2);
        z-index: -1;
      }

      &:hover, &:focus {
        &::after {
          transform: scaleX(1) scaleY(1);
          opacity: 1;
        }
      }

      @include from($component-breakpoint) {
        @include font(0);
        &::before {
          width: scale(2);
          height: scale(2);
        }
      }

      &--bildung {
        &::before {
          background-color: brand(3);
        }
      }
      &--gesundheit {
        &::before {
          background-color: brand(4);
        }
      }
      &--kultur {
        &::before {
          background-color: brand(1);
        }
      }
      &--sport {
        &::before {
          background-color: brand(2);
        }
      }
    }

    &__link-wrapper {
      position: relative;
      width: 100%;
    }

    &__content {
      position: relative;
      width: 100%;
      display: flex;
      flex-direction: column;
      @include font(1);
      @include grid-spacing(padding-left, 2);
      @include grid-spacing(padding-right, 3);
      margin-bottom: scale(12);

      @include from($component-breakpoint) {
        @include font(0);
        @include grid-spacing(padding-left, 1);
        @include grid-spacing(padding-right, 3);
      }

      @media only screen and (min-width: $max-width) {
        padding-left: 0;
      }

    }

    &:nth-child(even) {
      #{$component} {

        &__image {
          padding-right: 0;
          @include grid-spacing(padding-left, 3);
        }

        &__content {
          @include grid-spacing(padding-left, 3);
          @include grid-spacing(padding-right, 2);
        }
      }
    }

    @include from($component-breakpoint) {
      width: 50%;
      flex-shrink: 0;
      box-sizing: border-box;
      padding-left: scale(3);
      padding-right: scale(3);

      &:nth-child(4n+2) {
        padding-top: scale(10);

        &#{$component}--small {
          justify-content: center;
        }

        #{$component} {

          &__image {
            @include grid-spacing(padding-left, 1);
            @include grid-spacing(padding-right, 3);
          }

          &__content {
            @include grid-spacing(padding-left, 1);
            @include grid-spacing(padding-right, 3);
          }
        }
      }

      &:nth-child(4n+3) {
        padding-top: scale(10);

        &#{$component}--small {
          justify-content: center;
        }

        #{$component} {

          &__image {
            @include grid-spacing(padding-left, 3);
            @include grid-spacing(padding-right, 1);
          }

          &__content {
            @include grid-spacing(padding-right, 2);

            @include from(6) {
              @include grid-spacing(padding-left, 3);
            }
          }
        }
      }

      &:nth-child(4n+4) {
        #{$component} {
          &__image {
            @include grid-spacing(padding-left, 2);
          }

          &__content {
            @include grid-spacing(padding-left, 2);
            @include grid-spacing(padding-right, 1);
          }
        }
      }
    }
  }
</style>
